import { Component } from "@theme/component";
import { mediaQueryLarge } from "@theme/utilities";

const MOBILE_PER_PAGE = 12;
const DESKTOP_PER_PAGE = 24;
const HIDDEN_CLASS = "tile-paginator__item--hidden";
const PAGINATED_CLASS = "paginated";

/**
 * A custom element that paginates a list of <li> tiles by viewport-aware
 * per-page counts (12 mobile / 24 desktop). Expects:
 *   - a descendant <ul> with class "tile-paginator__grid" containing <li> tiles
 *   - a descendant <nav data-pagination> rendered by snippets/tile-paginator-nav.liquid
 *
 * Refs are intentionally NOT used here — Horizon's ref system scopes refs to
 * the nearest enclosing component, and an inner <results-list> element claims
 * `ref="grid"`. This element finds its grid and nav via querySelector.
 *
 * @extends Component
 */
export default class TilePaginator extends Component {
  /** @type {HTMLElement[]} */
  #items = [];

  /** @type {HTMLElement | null} */
  #grid = null;

  /** @type {HTMLElement | null} */
  #nav = null;

  /**
   * Index ranges — one per page — over `#items`, sized by grid cells rather
   * than element count so a multi-column in-grid tile doesn't push a page over
   * its row budget.
   *
   * @type {{ start: number, end: number }[]}
   */
  #pages = [];

  /**
   * Count of `#items` that are product results. In-grid content tiles share the
   * grid but aren't results, so the "Showing x of y" counter excludes them.
   */
  #productTotal = 0;

  #currentPage = 1;
  #perPage = 0;
  #totalPages = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
    mediaQueryLarge.addEventListener("change", this.#handleViewportChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener("change", this.#handleViewportChange);
    this.#nav?.removeEventListener("click", this.#handleNavClick);
  }

  /**
   * Re-initializes when the component subtree mutates — covers the case where
   * a filter change replaces the grid contents via morph instead of full
   * element replacement.
   */
  updatedCallback() {
    this.#init();
  }

  #init() {
    this.#grid = this.querySelector(".tile-paginator__grid");
    this.#nav = this.querySelector("[data-pagination]");

    if (!this.#grid) return;

    this.#items = Array.from(this.#grid.children);
    this.#productTotal = this.#items.filter(
      (item) => !item.dataset.gridSpan,
    ).length;
    this.#perPage = mediaQueryLarge.matches
      ? DESKTOP_PER_PAGE
      : MOBILE_PER_PAGE;
    this.#currentPage = 1;
    this.#pages = this.#buildPages();
    this.#totalPages = this.#pages.length;

    // Always release the FOUC cap, even when there's only one page, so tiles
    // beyond the per-page count don't stay hidden by the pre-hydration CSS.
    this.#grid.classList.add(PAGINATED_CLASS);

    if (this.#totalPages <= 1) {
      // Make sure no leftover hidden classes from a previous init persist.
      for (const item of this.#items) {
        item.classList.remove(HIDDEN_CLASS);
      }
      this.#nav?.classList.add("hidden");
      this.#nav?.classList.remove("flex");
      return;
    }

    this.#applyVisibility();
    this.#nav.classList.replace("hidden", "flex");
    this.#nav.removeEventListener("click", this.#handleNavClick);
    this.#nav.addEventListener("click", this.#handleNavClick);
    this.#renderNav();
  }

  #handleViewportChange = () => {
    const next = mediaQueryLarge.matches ? DESKTOP_PER_PAGE : MOBILE_PER_PAGE;
    if (next === this.#perPage) return;
    this.#perPage = next;
    this.#currentPage = 1;
    this.#pages = this.#buildPages();
    this.#totalPages = this.#pages.length;

    if (this.#totalPages <= 1) {
      for (const item of this.#items) {
        item.classList.remove(HIDDEN_CLASS);
      }
      this.#nav?.classList.add("hidden");
      this.#nav?.classList.remove("flex");
      return;
    }

    this.#applyVisibility();
    this.#nav?.classList.replace("hidden", "flex");
    this.#renderNav();
  };

  #handleNavClick = (event) => {
    const target = event.target.closest("[data-page]");
    if (!target) return;
    event.preventDefault();
    this.#goToPage(parseInt(target.dataset.page, 10));
  };

  #goToPage(page) {
    if (!Number.isInteger(page) || page < 1 || page > this.#totalPages) return;
    this.#currentPage = page;
    this.#applyVisibility();
    this.#renderNav();
    this.#grid?.scrollIntoView({ block: "start" });
  }

  /**
   * Splits `#items` into pages of at most `#perPage` grid cells. Tiles declare
   * how many cells they occupy via `data-grid-span`; product cards omit it and
   * count as one. Spans only apply on the 3-column desktop grid — below 768px
   * the grid is 1 or 2 columns and every tile occupies a single cell.
   *
   * @returns {{ start: number, end: number }[]}
   */
  #buildPages() {
    const spansApply = mediaQueryLarge.matches;
    const pages = [];
    let start = 0;
    let cells = 0;

    for (let i = 0; i < this.#items.length; i++) {
      const span = spansApply
        ? Number(this.#items[i].dataset.gridSpan) || 1
        : 1;
      if (cells > 0 && cells + span > this.#perPage) {
        pages.push({ start, end: i });
        start = i;
        cells = 0;
      }
      cells += span;
    }

    if (start < this.#items.length) {
      pages.push({ start, end: this.#items.length });
    }

    return pages;
  }

  #applyVisibility() {
    const page = this.#pages[this.#currentPage - 1];
    if (!page) return;
    this.#items.forEach((item, i) => {
      item.classList.toggle(HIDDEN_CLASS, i < page.start || i >= page.end);
    });
  }

  #renderNav() {
    if (!this.#nav) return;

    const slots = {
      prev: this.#nav.querySelector('[data-slot="prev"]'),
      next: this.#nav.querySelector('[data-slot="next"]'),
      pages: this.#nav.querySelector('[data-slot="pages"]'),
      counter: this.#nav.querySelector('[data-slot="counter"]'),
    };

    slots.prev.replaceChildren(this.#renderArrow("prev"));
    slots.next.replaceChildren(this.#renderArrow("next"));
    slots.pages.replaceChildren(...this.#renderPages());

    // Pages are sized in grid cells, so their element count varies with the
    // spans they hold — the running total has to come from the page range
    // rather than `currentPage * perPage`, and it counts results only.
    const end = this.#pages[this.#currentPage - 1]?.end ?? this.#items.length;
    const shown = this.#items
      .slice(0, end)
      .filter((item) => !item.dataset.gridSpan).length;
    slots.counter.textContent = `Showing ${shown} of ${this.#productTotal}`;
  }

  #renderArrow(direction) {
    const node = this.#cloneTemplate(direction);
    const link = node.querySelector("a");
    const targetPage =
      direction === "prev" ? this.#currentPage - 1 : this.#currentPage + 1;

    if (targetPage >= 1 && targetPage <= this.#totalPages) {
      link.dataset.page = targetPage;
    } else {
      link.setAttribute("aria-disabled", "true");
      link.removeAttribute("href");
    }
    return node;
  }

  #renderPages() {
    return this.#getPageNumbers().map((p) => {
      if (p === "...") return this.#cloneTemplate("page-ellipsis");

      const isCurrent = p === this.#currentPage;
      const node = this.#cloneTemplate(
        isCurrent ? "page-current" : "page-link",
      );
      const target = node.querySelector(isCurrent ? "span" : "a");
      target.textContent = p;
      if (!isCurrent) {
        target.dataset.page = p;
        target.setAttribute("aria-label", `Page ${p}`);
      }
      return node;
    });
  }

  #cloneTemplate(name) {
    return this.#nav
      .querySelector(`[data-template="${name}"]`)
      .content.cloneNode(true);
  }

  #getPageNumbers() {
    const total = this.#totalPages;
    const current = this.#currentPage;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages = new Set([1, total]);
    for (
      let i = Math.max(2, current - 1);
      i <= Math.min(total - 1, current + 1);
      i++
    ) {
      pages.add(i);
    }
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push("...");
      result.push(sorted[i]);
    }
    return result;
  }
}

if (!customElements.get("tile-paginator")) {
  customElements.define("tile-paginator", TilePaginator);
}
